/* @jsx h */
import { computed, defineComponent, h, ref, watch } from "vue";
import { useLocalization, QRCodeComponent } from "@lemmonhq/uikit";
import type { CollectedResponse } from "@app/server/client";
import { coinIconOf } from "../../components";
import { HowToCard } from "./HowToCard";
import { MembershipWalletCard } from "./MembershipWalletCard";
import { PlaceStackCard } from "./PlaceStackCard";
import { WalletBadges } from "./WalletBadges";
import { WalletCard } from "./WalletCard";
import { WalletCardStack } from "./WalletCardStack";
import { COLLECTED_BOTTOM_NAV_DESKTOP_CLEARANCE } from "./CollectedBottomNav";
import type { CoinCardItem, Place } from "./placeTypes";
import { passPlatformEnabled } from "./passDesign";

const HOWTO_KEY = "__howto__";

export type CollectedWalletStageProps = {
  places: Place[];
  totals?: CollectedResponse["totals"];
  /** Card key (`coin:*` / `mem:*`) to pass poster image URL. */
  images: Record<string, string>;
  view: "stack" | "grid";
  isMobile: boolean;
  focusPlaceKey?: string | null;
  onChangeView: (view: "stack" | "grid") => void;
  onOpenPlace: (key: string, source: HTMLElement | null) => void;
};

const placeIcon = (place: Place) => {
  const first = place.items[0];
  if (!first) return "storefront";
  return first.kind === "coin"
    ? coinIconOf(first.program.icon)
    : "card_membership";
};

const placeLabels = (place: Place) =>
  place.items.map((item) =>
    item.kind === "coin" ? item.program.name : item.pass.program_name,
  );

export const CollectedWalletStage = defineComponent<CollectedWalletStageProps>({
  name: "CollectedWalletStage",
  props: [
    "places",
    "totals",
    "images",
    "view",
    "isMobile",
    "focusPlaceKey",
    "onChangeView",
    "onOpenPlace",
  ],
  setup(props) {
    const { t } = useLocalization();
    const imageFor = (passKey: string) => props.images[passKey] ?? null;
    // A place stack wears its front pass's own artwork; it never borrows
    // another program's design to dress the whole place.
    const placeImage = (place: Place) =>
      place.items[0] ? imageFor(place.items[0].key) : null;

    const placeByKey = (key: string | null | undefined) =>
      key ? (props.places.find((place) => place.key === key) ?? null) : null;

    const coinFace = (item: CoinCardItem) => (
      <WalletCard
        program={item.program}
        shopName={item.shop.name}
        wide={true}
        qrValue={`lemmon-loyalty:${item.shop.profile_id}`}
      />
    );

    const deck = computed(() => [
      ...props.places.map((place) => {
        if (place.items.length > 1) {
          return {
            key: place.key,
            render: () => (
              <PlaceStackCard
                name={place.name}
                imageUrl={placeImage(place)}
                icon={placeIcon(place)}
                labels={placeLabels(place)}
                wide={true}
              />
            ),
          };
        }
        const item = place.items[0];
        if (item.kind === "membership") {
          return {
            key: place.key,
            render: () => <MembershipWalletCard pass={item.pass} wide={true} />,
            renderBack: () => (
              <MembershipWalletCard
                pass={item.pass}
                wide={true}
                largeQr={true}
              />
            ),
          };
        }
        return {
          key: place.key,
          render: () => coinFace(item),
          renderBack: () => (
            <WalletCard
              program={item.program}
              shopName={item.shop.name}
              wide={true}
              back={true}
              center={() => (
                <div class="w-full max-w-[12rem] rounded-xl bg-white p-3 shadow-lg">
                  <div class="aspect-square w-full">
                    <QRCodeComponent
                      url={`lemmon-loyalty:${item.shop.profile_id}`}
                    />
                  </div>
                </div>
              )}
            />
          ),
        };
      }),
      { key: HOWTO_KEY, render: () => <HowToCard wide={true} /> },
    ]);

    const frontKey = ref<string | null>(null);
    const frontItem = computed(() => {
      const place = placeByKey(frontKey.value ?? props.places[0]?.key);
      return place?.items.length === 1 ? place.items[0] : null;
    });
    const frontWallet = computed(() => {
      const item = frontItem.value;
      if (!item) return null;
      return item.kind === "coin" ? item.walletPass : item.pass;
    });

    const stackArea = ref<HTMLElement | null>(null);
    const renderStack = () => (
      <div
        ref={stackArea}
        class={[
          "flex min-h-0 flex-col space-y-2.5 px-4",
          props.isMobile ? "flex-1" : "",
        ]}
      >
        <WalletCardStack
          fill={props.isMobile}
          items={deck.value}
          focusKey={props.focusPlaceKey}
          onSelect={(key: string) => {
            const place = placeByKey(key);
            if (place && place.items.length > 1) {
              props.onOpenPlace(key, stackArea.value);
            }
          }}
          onFrontChange={(key: string) => (frontKey.value = key)}
        />
        <div
          class={[
            "flex shrink-0 items-center justify-center gap-2",
            props.isMobile ? "min-h-7" : "min-h-9",
          ]}
        >
          {frontKey.value !== HOWTO_KEY && frontItem.value && (
            <WalletBadges
              compact={props.isMobile}
              appleUrl={
                !frontWallet.value ||
                !passPlatformEnabled(
                  frontWallet.value.kind,
                  frontWallet.value.design,
                  "apple",
                )
                  ? null
                  : `/api/v3/customer/wallet/pass/${frontWallet.value.serial}.pkpass`
              }
              googleUrl={
                !frontWallet.value ||
                !passPlatformEnabled(
                  frontWallet.value.kind,
                  frontWallet.value.design,
                  "google",
                )
                  ? null
                  : (frontWallet.value.google_save_url ??
                    `/api/v3/customer/wallet/google/${frontWallet.value.serial}`)
              }
            />
          )}
        </div>
      </div>
    );

    const gridFocusElement = ref<HTMLElement | null>(null);
    const setGridFocusElement = (key: string) => (element: unknown) => {
      if (key === props.focusPlaceKey) {
        gridFocusElement.value = (element ?? null) as HTMLElement | null;
      }
    };
    watch(
      [gridFocusElement, () => props.view],
      () => {
        if (gridFocusElement.value && props.view === "grid") {
          gridFocusElement.value.scrollIntoView({
            block: "center",
            behavior: "smooth",
          });
        }
      },
      { flush: "post" },
    );

    const renderGridPlace = (place: Place) => {
      if (place.items.length > 1) {
        return (
          <PlaceStackCard
            name={place.name}
            imageUrl={placeImage(place)}
            icon={placeIcon(place)}
            labels={placeLabels(place)}
            wide={true}
          />
        );
      }
      const item = place.items[0];
      if (item.kind === "membership") {
        return <MembershipWalletCard pass={item.pass} wide={true} />;
      }
      return (
        <WalletCard
          program={item.program}
          shopName={item.shop.name}
          wide={true}
          qrValue={`lemmon-loyalty:${item.shop.profile_id}`}
        />
      );
    };

    const gridCard = (
      key: string,
      imageUrl: string | null,
      card: () => any,
      focused = false,
    ) => (
      <div
        key={key}
        ref={setGridFocusElement(key)}
        role="button"
        tabindex={0}
        aria-label={t("customer.collected.show_card")}
        onClick={(event: MouseEvent) =>
          props.onOpenPlace(key, event.currentTarget as HTMLElement)
        }
        onKeydown={(event: KeyboardEvent) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          props.onOpenPlace(key, event.currentTarget as HTMLElement);
        }}
        class={[
          "group relative isolate h-full cursor-pointer rounded-3xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
          focused
            ? "ring-2 ring-primary ring-offset-2 ring-offset-base-100"
            : "",
        ]}
      >
        {/* Match the membership welcome email: two copies of the card's own
            cover photo sit behind the face, dimmed and fanned at opposite
            angles. They are decoration only, and stay hidden on the phone
            where the swipe deck already supplies this depth. */}
        <div
          aria-hidden="true"
          class="pointer-events-none absolute inset-0 hidden overflow-hidden rounded-3xl border border-base-300 bg-base-300 shadow-lg sm:block"
          style={{
            opacity: "0.5",
            transform: "translate(-13px, -16px) rotate(-5.5deg) scale(0.96)",
          }}
        >
          {imageUrl && (
            <img
              src={imageUrl}
              alt=""
              class="absolute inset-0 h-full w-full object-cover"
            />
          )}
          <div class="absolute inset-0 bg-black/30" />
        </div>
        <div
          aria-hidden="true"
          class="pointer-events-none absolute inset-0 hidden overflow-hidden rounded-3xl border border-base-300 bg-base-300 shadow-lg sm:block"
          style={{
            opacity: "0.38",
            transform: "translate(12px, -8px) rotate(4.5deg) scale(0.96)",
          }}
        >
          {imageUrl && (
            <img
              src={imageUrl}
              alt=""
              class="absolute inset-0 h-full w-full object-cover"
            />
          )}
          <div class="absolute inset-0 bg-black/35" />
        </div>
        <div class="relative z-10 h-full rounded-3xl transition-transform duration-300 ease-out group-hover:-translate-y-1">
          {card()}
        </div>
      </div>
    );

    const renderGrid = () => (
      <div
        class="grid auto-rows-[minmax(30rem,1fr)] grid-cols-2 gap-x-8 gap-y-10 px-5 pt-6 lg:grid-cols-3"
        style={{
          paddingBottom: `${COLLECTED_BOTTOM_NAV_DESKTOP_CLEARANCE}px`,
        }}
      >
        {props.places.map((place) =>
          gridCard(
            place.key,
            placeImage(place),
            () => renderGridPlace(place),
            place.key === props.focusPlaceKey,
          ),
        )}
        <HowToCard wide={true} />
      </div>
    );

    const stat = (icon: string, value: number, label: string) => (
      <div class="flex min-w-0 items-center justify-center gap-1.5 px-2 py-2">
        <span
          class="material-symbols-outlined text-sm leading-none text-base-content/35"
          aria-hidden="true"
        >
          {icon}
        </span>
        <span class="text-sm font-bold leading-none tabular-nums text-base-content">
          {value}
        </span>
        <span class="truncate text-[11px] leading-none text-base-content/50">
          {label}
        </span>
      </div>
    );

    return () => (
      <section
        class={[
          "flex min-h-0 flex-col gap-3 sm:gap-5",
          props.isMobile ? "flex-1" : "",
        ]}
        aria-label={t("customer.collected.title")}
      >
        <div class="flex shrink-0 items-stretch gap-2">
          <div class="grid min-w-0 flex-1 grid-cols-3 divide-x divide-base-300 overflow-hidden rounded-2xl border border-base-300 bg-base-100 shadow-sm my-2 mx-2">
            {stat(
              "toll",
              props.totals?.coins ?? 0,
              t("customer.collected.stat_coins"),
            )}
            {stat(
              "storefront",
              props.totals?.shops ?? 0,
              t("customer.collected.stat_shops"),
            )}
            {stat(
              "receipt_long",
              props.totals?.payments ?? 0,
              t("customer.collected.stat_visits"),
            )}
          </div>
          {!props.isMobile && (
            <button
              type="button"
              class="grid w-10 shrink-0 place-items-center rounded-2xl border border-base-300 bg-base-100 text-base-content/40 shadow-sm hover:bg-base-200 hover:text-base-content"
              aria-label={t(
                props.view === "grid"
                  ? "customer.collected.view_stack"
                  : "customer.collected.view_grid",
              )}
              title={t(
                props.view === "grid"
                  ? "customer.collected.view_stack"
                  : "customer.collected.view_grid",
              )}
              onClick={() =>
                props.onChangeView(props.view === "grid" ? "stack" : "grid")
              }
            >
              <span
                class="material-symbols-outlined text-lg"
                aria-hidden="true"
              >
                {props.view === "grid" ? "style" : "grid_view"}
              </span>
            </button>
          )}
        </div>
        {props.view === "grid" ? renderGrid() : renderStack()}
      </section>
    );
  },
});
